import { parseTaggedContent } from 'shared/utils/copilotTagParser';

function messageType(msg) {
  return msg?.type || msg?._getType?.() || '';
}

function getMessageText(raw) {
  if (typeof raw === 'string') return raw;
  if (Array.isArray(raw)) {
    return raw
      .filter(block => block.type === 'text')
      .map(block => block.text)
      .join('\n');
  }
  return '';
}

function extractTaggedThinking(text) {
  if (!text?.trim()) return '';
  return parseTaggedContent(text, true)
    .filter(segment => segment.type === 'thinking' && segment.content?.trim())
    .map(segment => segment.content.trim())
    .join('\n\n');
}

export function extractReasoningFromMessage(msg) {
  const raw = msg?.content;
  if (!raw) return '';

  if (Array.isArray(raw)) {
    const anthropicThinking = raw
      .filter(block => block.type === 'thinking' && block.thinking?.trim())
      .map(block => block.thinking.trim())
      .join('\n\n');
    if (anthropicThinking) return anthropicThinking;
    return extractTaggedThinking(getMessageText(raw));
  }

  if (typeof raw === 'string') {
    return extractTaggedThinking(raw);
  }

  return '';
}

export function messageHasResponseContent(msg) {
  const text = getMessageText(msg?.content);
  if (!text.trim()) return false;

  const segments = parseTaggedContent(text, true);
  if (
    segments.some(
      segment =>
        ['text', 'response', 'action'].includes(segment.type) &&
        segment.content?.trim()
    )
  ) {
    return true;
  }

  if (segments.length === 0 && text.trim()) return true;
  return false;
}

function getTurnBounds(msgs, index) {
  let turnStart = -1;
  for (let i = index; i >= 0; i -= 1) {
    if (messageType(msgs[i]) === 'human') {
      turnStart = i;
      break;
    }
  }

  let turnEnd = msgs.length - 1;
  for (let i = index + 1; i < msgs.length; i += 1) {
    if (messageType(msgs[i]) === 'human') {
      turnEnd = i - 1;
      break;
    }
  }

  return { turnStart, turnEnd };
}

function getTurnAiIndices(msgs, turnStart, turnEnd) {
  const indices = [];
  for (let i = turnStart + 1; i <= turnEnd; i += 1) {
    if (messageType(msgs[i]) === 'ai') indices.push(i);
  }
  return indices;
}

export function getTurnReasoningMeta(
  msgs,
  index,
  { isStreaming = false } = {}
) {
  if (messageType(msgs[index]) !== 'ai') {
    return {
      hideReasoning: false,
      mergedTurnReasoning: null,
      reasoningStreaming: false,
      suppressBubble: false,
    };
  }

  const { turnStart, turnEnd } = getTurnBounds(msgs, index);
  const aiIndices = getTurnAiIndices(msgs, turnStart, turnEnd);
  if (!aiIndices.length) {
    return {
      hideReasoning: false,
      mergedTurnReasoning: null,
      reasoningStreaming: false,
      suppressBubble: false,
    };
  }

  const lastAiIndex = aiIndices[aiIndices.length - 1];
  const isLeader = index === lastAiIndex;

  if (!isLeader) {
    const reasoningOnly =
      extractReasoningFromMessage(msgs[index]) &&
      !messageHasResponseContent(msgs[index]);
    return {
      hideReasoning: true,
      mergedTurnReasoning: null,
      reasoningStreaming: false,
      suppressBubble: reasoningOnly,
    };
  }

  const mergedTurnReasoning =
    aiIndices
      .map(i => extractReasoningFromMessage(msgs[i]))
      .filter(Boolean)
      .join('\n\n') || null;

  const lastAiIsStreaming = isStreaming && lastAiIndex === msgs.length - 1;
  const reasoningStreaming =
    lastAiIsStreaming &&
    !!mergedTurnReasoning &&
    !messageHasResponseContent(msgs[lastAiIndex]);

  return {
    hideReasoning: false,
    mergedTurnReasoning,
    reasoningStreaming,
    suppressBubble: false,
  };
}
